import filter from "lodash/filter";
import first from "lodash/first";
import { Division, Gender, Lifter, Meet } from "types";
import {
  getDivisionDoc,
  getLifterAwardsWeightClassDoc,
  getPlace,
  getScore,
} from "./lifterHelper";
import get from "lodash/get";
import sortBy from "lodash/sortBy";
import sumBy from "lodash/sumBy";

function isNotNull<T>(value: T | null): value is T {
  return value !== null;
}

export const TEAM_SCORING_MAX_LIFTERS_DEFAULT = 5;
export const TEAM_SCORING_FORMULA_DEFAULT = "DOTS_POINTS";
export const TEAM_SCORING_METHOD_DEFAULT = "BY_PLACING";
export const TEAM_SCORING_POINTS_MAP_DEFAULT: Record<number, number> = {
  1: 12,
  2: 9,
  3: 8,
  4: 7,
  5: 6,
  6: 5,
  7: 4,
  8: 3,
  9: 2,
};

export const getTeamLifters = ({
  meet,
  teamName,
  gender,
}: {
  meet: Meet;
  teamName: string;
  gender?: Gender;
}) => {
  const teamLifters = filter(meet.lifters, { team: teamName });
  if (gender) {
    return filter(teamLifters, { gender });
  }
  return teamLifters;
};

export type TeamPoints = {
  score: number;
  lifters: (
    | Lifter
    | {
        teamPointsContributed: number | null;
        place: number;
        formulaTieBreakerPoints: number;
        division: Division;
        weightClass: string;
      }
  )[];
};

const getPlacingTeamPoints = ({
  meet,
  teamName,
  gender,
  forecasted,
}: {
  meet: Meet;
  teamName: string;
  gender?: Gender;
  forecasted?: boolean;
}): TeamPoints => {
  const teamSize =
    meet.teamScoringMaxLifters ?? TEAM_SCORING_MAX_LIFTERS_DEFAULT;
  const teamLifters = getTeamLifters({ meet, teamName, gender });
  const placings = teamLifters.map((lifter) => {
    const lifterDivision = first(lifter.divisions);
    if (!lifterDivision) {
      return null;
    }

    const division = getDivisionDoc(lifterDivision.divisionId, meet);

    if (!division) {
      return null;
    }

    const formulaTieBreakerPoints = getScore({
      lifter,
      division: {
        ...division,
        scoreBy: meet.teamScoringFormula ?? TEAM_SCORING_FORMULA_DEFAULT,
      },
      meet,
      forecasted,
    });

    const place = getPlace(lifter, lifterDivision, meet, forecasted);
    const pointsMap =
      meet.teamScoringPlacingValues ?? TEAM_SCORING_POINTS_MAP_DEFAULT;
    return {
      teamPointsContributed: place
        ? get(pointsMap, place, TEAM_SCORING_POINTS_MAP_DEFAULT[place] ?? 1)
        : null,
      place,
      formulaTieBreakerPoints,
      division,
      ...lifter,
      weightClass: getLifterAwardsWeightClassDoc(meet, lifter, lifterDivision)
        ?.name,
    };
  });

  const successfulPlacings = placings.filter(isNotNull);

  const sortedPlacings = sortBy(successfulPlacings, "place");

  // Point scoring for all World, Continental and Regional Championships shall be: 12, 9, 8, 7, 6, 5, 4, 3, 2 for the first
  // 9 placing in any bodyweight category. Thereafter, each lifter who makes a total in the competition shall be awarded
  // one point. Point scoring for all national competitions shall be at the discretion of the national federation.

  // In the case of a tie for the classification of a team or a nation, the team having
  // the largest number of first places will be ranked first. In the case of a tie between two nations having the same number
  // of first places, the one having the most second places will be classified first, and so on through the placing of the
  // maximum of five scoring lifters. Should teams or Nations finish equally after this procedure has been applied,
  // then the team or Nation with the greater total number of IPF points will be declared the higher placed.

  const withAdjustedPoints = sortedPlacings.map((lifter, index) => {
    if (index < teamSize) {
      return lifter;
    }

    return { ...lifter, teamPointsContributed: 0, formulaTieBreakerPoints: 0 };
  });

  const score = sumBy(withAdjustedPoints, "teamPointsContributed");

  return { score, lifters: withAdjustedPoints };
};

const getFormulaTeamPoints = ({
  meet,
  teamName,
  gender,
  forecasted,
}: {
  meet: Meet;
  teamName: string;
  gender?: Gender;
  forecasted?: boolean;
}): TeamPoints => {
  const teamSize = meet.teamScoringMaxLifters ?? 5;
  const teamLifters = getTeamLifters({ meet, teamName, gender });
  const placings = teamLifters.map((lifter) => {
    const lifterDivision = first(lifter.divisions);
    if (!lifterDivision) {
      return null;
    }

    const division = getDivisionDoc(lifterDivision.divisionId, meet);

    if (!division) {
      return null;
    }

    const points = getScore({
      lifter,
      division: {
        ...division,
        scoreBy: meet.teamScoringFormula ?? TEAM_SCORING_FORMULA_DEFAULT,
      },
      meet,
      forecasted,
    });
    const place = getPlace(lifter, lifterDivision, meet, forecasted);

    return {
      teamPointsContributed: points,
      formulaTieBreakerPoints: points,
      place,
      division,
      ...lifter,
      weightClass: getLifterAwardsWeightClassDoc(meet, lifter, lifterDivision)
        ?.name,
    };
  });

  const successfulPlacings = placings.filter(isNotNull);

  const sortedPlacings = sortBy(successfulPlacings, "place");

  const withAdjustedPoints = sortedPlacings.map((lifter, index) => {
    if (index < teamSize) {
      return lifter;
    }

    return { ...lifter, teamPointsContributed: 0, formulaTieBreakerPoints: 0 };
  });

  const score = sumBy(withAdjustedPoints, "teamPointsContributed");

  return { score, lifters: withAdjustedPoints };
};

export const getTeamPoints = ({
  meet,
  teamName,
  gender,
  forecasted,
}: {
  meet: Meet;
  teamName: string;
  gender?: Gender;
  forecasted?: boolean;
}): TeamPoints => {
  return meet.teamScoringMethod === "BY_FORMULA"
    ? getFormulaTeamPoints({ meet, teamName, gender, forecasted })
    : getPlacingTeamPoints({ meet, teamName, gender, forecasted });
};
